import {
  SISTER_SEARCHER_RESULT_LIMIT,
  SISTER_SEARCHER_PERFORMANCE_LIMIT,
  SISTER_SEARCHER_SIMILARITY_OPTIONS,
  SISTER_SEARCHER_VESSEL_OPTION_LIMIT,
} from "../constants/sister-searcher.constants";
import {
  getMockSisterSearcherPerformance,
  getMockSisterSearcherVessels,
  searchMockSisterSearcherVessels,
} from "../mocks/sister-searcher.mocks";
import {
  SisterSearcherInImosFilter,
  SisterSearcherPerformance,
  SisterSearcherRequestStatus,
  SisterSearcherSearchRequest,
  SisterSearcherSearchResponse,
  SisterSearcherSimilarityKey,
  SisterSearcherVesselOption,
} from "../models/sister-searcher.models";
import {
  getSisterSearcherPerformance,
  getSisterSearcherVessels,
  searchSisterSearcherVessels,
} from "../services/sister-searcher.service";
import {
  sisterSearcherInImosAtom,
  sisterSearcherMockEnabledAtom,
  sisterSearcherSelectedImoAtom,
  sisterSearcherSimilarityAtom,
  sisterSearcherViewAtom,
} from "../state/sister-searcher.atoms";
import { getSisterSearcherErrorMessage } from "../utils/sister-searcher-format.utils";
import axios from "axios";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";

// Owns the Sister Searcher requests and aborts superseded work so revisiting or quickly changing the search cannot flash stale data.
// Keeps the Landing, Main, and Performance bookmark states separate while preserving the report's vessel and filter selections.
export const useSisterSearcher = () => {
  const useMockData = useAtomValue(sisterSearcherMockEnabledAtom);
  const [similarity, setSimilarity] = useAtom(sisterSearcherSimilarityAtom);
  const [inImos, setInImos] = useAtom(sisterSearcherInImosAtom);
  const [selectedImo, setSelectedImo] = useAtom(sisterSearcherSelectedImoAtom);
  const [view, setView] = useAtom(sisterSearcherViewAtom);
  const [vesselQuery, setVesselQuery] = useState("");
  const [vesselOptions, setVesselOptions] = useState<SisterSearcherVesselOption[]>([]);
  const [vesselStatus, setVesselStatus] = useState<SisterSearcherRequestStatus>("idle");
  const [vesselError, setVesselError] = useState("");
  const [searchResult, setSearchResult] = useState<SisterSearcherSearchResponse | null>(null);
  const [searchStatus, setSearchStatus] = useState<SisterSearcherRequestStatus>("idle");
  const [searchError, setSearchError] = useState("");
  const [searchRevision, setSearchRevision] = useState(0);
  const [performance, setPerformance] = useState<SisterSearcherPerformance[]>([]);
  const [performanceStatus, setPerformanceStatus] = useState<SisterSearcherRequestStatus>("idle");
  const [performanceError, setPerformanceError] = useState("");
  const searchAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setVesselOptions([]);
    setVesselStatus("loading");
    setVesselError("");
    const timeout = window.setTimeout(async () => {
      try {
        const vessels = useMockData
          ? getMockSisterSearcherVessels(vesselQuery, SISTER_SEARCHER_VESSEL_OPTION_LIMIT)
          : (
              await getSisterSearcherVessels(
                vesselQuery.trim(),
                SISTER_SEARCHER_VESSEL_OPTION_LIMIT,
                controller.signal,
              )
            ).vessels;

        if (!controller.signal.aborted) {
          setVesselOptions(vessels);
          setVesselStatus("success");
        }
      } catch (error) {
        if (!axios.isCancel(error) && !controller.signal.aborted) {
          setVesselOptions([]);
          setVesselStatus("error");
          setVesselError(getSisterSearcherErrorMessage(error, "Vessels could not be loaded."));
        }
      }
    }, 250);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [useMockData, vesselQuery]);

  useEffect(() => {
    searchAbortRef.current?.abort();
    setSearchResult(null);
    setSearchStatus("idle");
    setSearchError("");
    setPerformance([]);
    setPerformanceStatus("idle");
    setPerformanceError("");
    setView("landing");
  }, [setView, useMockData]);

  useEffect(() => {
    if (view !== "main" || !selectedImo) {
      return;
    }

    searchAbortRef.current?.abort();
    const controller = new AbortController();
    searchAbortRef.current = controller;
    setSearchResult(null);
    setSearchStatus("loading");
    setSearchError("");
    setPerformance([]);
    setPerformanceStatus("idle");
    setPerformanceError("");

    const request: SisterSearcherSearchRequest = {
      imo: selectedImo,
      ...similarity,
      inImos,
      limit: SISTER_SEARCHER_RESULT_LIMIT,
    };

    const loadSisterVessels = async () => {
      try {
        const result = useMockData
          ? searchMockSisterSearcherVessels(request)
          : await searchSisterSearcherVessels(request, controller.signal);

        if (!controller.signal.aborted) {
          setSearchResult({
            ...result,
            sisterVessels: [...result.sisterVessels].sort(
              (left, right) => (right.build ?? 0) - (left.build ?? 0),
            ),
          });
          setSearchStatus("success");
        }
      } catch (error) {
        if (!axios.isCancel(error) && !controller.signal.aborted) {
          setSearchResult(null);
          setSearchStatus("error");
          setSearchError(getSisterSearcherErrorMessage(error, "Sister vessels could not be loaded."));
        }
      }
    };

    void loadSisterVessels();

    return () => controller.abort();
  }, [inImos, searchRevision, selectedImo, similarity, useMockData, view]);

  useEffect(() => {
    if (view !== "performance" || !searchResult?.selectedVessel) {
      return;
    }

    const controller = new AbortController();
    const imos = Array.from(
      new Set([searchResult.selectedVessel, ...searchResult.sisterVessels].map((vessel) => vessel.imo)),
    ).slice(0, SISTER_SEARCHER_PERFORMANCE_LIMIT);
    setPerformanceStatus("loading");
    setPerformanceError("");

    const loadPerformance = async () => {
      try {
        const rows = useMockData
          ? getMockSisterSearcherPerformance(imos)
          : (await getSisterSearcherPerformance(imos, controller.signal)).performance;

        if (!controller.signal.aborted) {
          setPerformance(rows);
          setPerformanceStatus("success");
        }
      } catch (error) {
        if (!axios.isCancel(error) && !controller.signal.aborted) {
          setPerformance([]);
          setPerformanceStatus("error");
          setPerformanceError(getSisterSearcherErrorMessage(error, "Performance data could not be loaded."));
        }
      }
    };

    void loadPerformance();

    return () => controller.abort();
  }, [searchResult, useMockData, view]);

  useEffect(
    () => () => {
      searchAbortRef.current?.abort();
    },
    [],
  );

  const allSimilarityOptionsSelected = SISTER_SEARCHER_SIMILARITY_OPTIONS.every(
    ({ key }) => similarity[key],
  );

  const toggleAllSimilarityOptions = useCallback(() => {
    setSimilarity((current) => {
      const shouldSelect = !SISTER_SEARCHER_SIMILARITY_OPTIONS.every(({ key }) => current[key]);

      return {
        ...current,
        sameCountryOfBuild: shouldSelect,
        sameDesign: shouldSelect,
        sameDwt: shouldSelect,
        sameEngine: shouldSelect,
        sameYard: shouldSelect,
      };
    });
  }, [setSimilarity]);

  const toggleSimilarityOption = useCallback(
    (key: SisterSearcherSimilarityKey) => {
      setSimilarity((current) => ({ ...current, [key]: !current[key] }));
    },
    [setSimilarity],
  );

  const setYearsBefore = useCallback(
    (yearsBefore: number) => setSimilarity((current) => ({ ...current, yearsBefore })),
    [setSimilarity],
  );

  const setYearsAfter = useCallback(
    (yearsAfter: number) => setSimilarity((current) => ({ ...current, yearsAfter })),
    [setSimilarity],
  );

  const setInImosSelection = useCallback(
    (selection: SisterSearcherInImosFilter) => setInImos(selection),
    [setInImos],
  );

  const openPerformanceReview = useCallback(() => {
    setPerformance([]);
    setPerformanceStatus("loading");
    setPerformanceError("");
    setView("performance");
  }, [setView]);

  const openSisterSearch = useCallback(() => {
    if (selectedImo) {
      setSearchRevision((current) => current + 1);
      setView("main");
    }
  }, [selectedImo, setView]);

  const openLanding = useCallback(() => setView("landing"), [setView]);

  return {
    allSimilarityOptionsSelected,
    inImos,
    performance,
    performanceError,
    performanceStatus,
    openLanding,
    openPerformanceReview,
    openSisterSearch,
    searchError,
    searchResult,
    searchStatus,
    selectedImo,
    setSelectedImo,
    setInImosSelection,
    setVesselQuery,
    setView,
    setYearsAfter,
    setYearsBefore,
    similarity,
    toggleAllSimilarityOptions,
    toggleSimilarityOption,
    useMockData,
    vesselError,
    vesselOptions,
    vesselQuery,
    vesselStatus,
    view,
  };
};
